<template>
  <AppLayout>
    <div v-if="!loadingBoard" :key="boardId" class="max-w-full mx-auto">
      <!-- Board header -->
      <section class="move-board-header mb-5 sm:mb-6">
        <div
          class="move-board-header__glow pointer-events-none"
          aria-hidden="true"
        />

        <div class="move-board-header__top">
          <div class="min-w-0 flex-1">
            <p class="move-board-header__eyebrow">Board</p>
            <div v-if="!isEditingBoardName" class="flex items-center gap-1.5 min-w-0 group/title">
              <h1 class="move-board-header__title truncate">{{ board?.name || 'Board' }}</h1>
              <button
                v-if="isBoardOwner"
                type="button"
                class="move-board-header__edit"
                aria-label="Rename board"
                @click="startEditingBoardName"
              >
                <span class="material-icons text-base">edit</span>
              </button>
            </div>
            <div v-else class="flex items-center gap-2 w-full min-w-0 mt-0.5">
              <input
                ref="boardNameInput"
                v-model="newBoardName"
                type="text"
                class="move-board-title"
                @keyup.enter="saveBoardName"
                @keyup.esc="cancelEditingBoardName"
              />
              <button type="button" class="move-icon-btn shrink-0" aria-label="Save name" @click="saveBoardName">
                <span class="material-icons">check</span>
              </button>
              <button type="button" class="move-icon-btn shrink-0" aria-label="Cancel" @click="cancelEditingBoardName">
                <span class="material-icons">close</span>
              </button>
            </div>
            <p class="move-board-header__meta">
              {{ lists.length }} {{ lists.length === 1 ? 'list' : 'lists' }}
              <span aria-hidden="true">·</span>
              {{ activeTaskCount }} {{ activeTaskCount === 1 ? 'task' : 'tasks' }}
              <template v-if="archivedTaskCount">
                <span aria-hidden="true">·</span>
                {{ archivedTaskCount }} archived
              </template>
              <span aria-hidden="true">·</span>
              {{ members.length }} {{ members.length === 1 ? 'member' : 'members' }}
            </p>
          </div>

          <div class="move-board-header__actions">
            <button
              type="button"
              class="move-board-header__action"
              :class="{ 'is-active': showArchived }"
              @click="showArchived = !showArchived"
            >
              <span class="material-icons text-lg">{{ showArchived ? 'unarchive' : 'archive' }}</span>
              <span>{{ showArchived ? 'Active tasks' : 'Archived' }}</span>
              <span v-if="archivedTaskCount" class="move-board-header__badge">{{ archivedTaskCount }}</span>
            </button>
            <button
              type="button"
              class="move-board-header__action"
              @click="showInviteModal = true"
            >
              <span class="material-icons text-lg">person_add</span>
              <span>Invite</span>
            </button>
            <button
              type="button"
              class="move-board-header__action"
              @click="showMembersModal = true"
            >
              <span class="material-icons text-lg">group</span>
              <span>Members</span>
              <span v-if="members.length" class="move-board-header__badge">{{ members.length }}</span>
            </button>
          </div>
        </div>

        <form class="move-board-header__add-list" @submit.prevent="addList">
          <span class="material-icons move-board-header__add-icon">view_column</span>
          <input
            v-model="newListName"
            type="text"
            placeholder="Add a list…"
            class="move-board-header__add-input"
            required
          />
          <button type="submit" class="move-board-header__add-btn">
            <span class="material-icons text-lg">add</span>
            <span class="hidden sm:inline">Add list</span>
          </button>
        </form>
      </section>

      <div v-if="isSyncingTasks" class="move-kanban-sync-banner" role="status" aria-live="polite">
        <span class="material-icons animate-spin text-base">sync</span>
        Saving task order…
      </div>

      <!-- Lists -->
      <div class="move-kanban-scroll overflow-x-auto pb-4 -mx-1 sm:mx-0">
        <div v-if="lists.length === 0" class="move-empty-state py-12">
          <span class="material-icons">view_list</span>
          <p class="font-medium move-text">No lists yet</p>
          <p class="text-sm move-text-faint mt-1">Add your first list above to get started.</p>
        </div>
        <draggable
          v-model="lists"
          group="lists"
          :animation="200"
          item-key="id"
          @end="onListDrop"
          :disabled="isMobile || editingListId !== null"
          :filter="'.move-kanban-nodrag'"
          :prevent-on-filter="true"
          class="flex gap-4 sm:gap-6 min-w-max px-2 sm:px-0"
        >
          <template #item="{ element: list, index }">
            <transition name="list-slide" appear>
              <article class="move-kanban-column group/column flex-shrink-0 flex flex-col" :class="{ 'move-kanban-column--syncing': isListSyncing(list.id), 'move-kanban-column--drag-over': dragOverListId === list.id }">
                <header class="move-kanban-column__header">
                  <div class="flex items-center gap-2 min-w-0 flex-1">
                    <div v-if="isMobile" class="flex flex-col gap-0.5 shrink-0 move-kanban-nodrag">
                      <button
                        type="button"
                        class="move-icon-btn !p-0.5"
                        :disabled="index === 0"
                        @click="moveListUp(list.id, index)"
                      >
                        <span class="material-icons text-sm">arrow_upward</span>
                      </button>
                      <button
                        type="button"
                        class="move-icon-btn !p-0.5"
                        :disabled="index === lists.length - 1"
                        @click="moveListDown(list.id, index)"
                      >
                        <span class="material-icons text-sm">arrow_downward</span>
                      </button>
                    </div>
                    <form
                      v-if="editingListId === list.id"
                      class="min-w-0 flex-1 move-kanban-nodrag"
                      @submit.prevent="saveListName(list.id)"
                    >
                      <input
                        ref="listNameInput"
                        v-model="editingListName"
                        type="text"
                        class="move-kanban-column__title-input"
                        maxlength="255"
                        aria-label="List name"
                        @keydown.esc.prevent="cancelEditingListName"
                        @blur="saveListName(list.id)"
                        @click.stop
                        @mousedown.stop
                      />
                    </form>
                    <button
                      v-else
                      type="button"
                      class="move-kanban-column__title move-kanban-nodrag"
                      :title="`Rename ${list.name}`"
                      @click.stop="startEditingListName(list)"
                    >
                      {{ list.name }}
                    </button>
                    <span v-if="editingListId !== list.id" class="move-kanban-count">{{ getListTaskCount(list.id) }}</span>
                    <span v-if="isListSyncing(list.id)" class="move-kanban-sync-indicator" aria-label="Saving">
                      <span class="material-icons animate-spin text-sm">sync</span>
                    </span>
                  </div>
                  <div v-if="!showArchived" class="move-kanban-column__actions move-kanban-nodrag">
                    <button
                      type="button"
                      class="move-kanban-add-btn"
                      :class="{ 'is-active': addingTaskToList === list.id }"
                      aria-label="Add task"
                      @click="openAddTask(list.id)"
                    >
                      <span class="material-icons text-base">add</span>
                    </button>
                    <button
                      type="button"
                      class="move-kanban-delete"
                      aria-label="Delete list"
                      @click="deleteList(list.id)"
                    >
                      <span class="material-icons text-base">delete_outline</span>
                    </button>
                  </div>
                  <div v-else class="move-kanban-column__actions move-kanban-nodrag">
                    <button
                      type="button"
                      class="move-kanban-delete"
                      aria-label="Delete list"
                      @click="deleteList(list.id)"
                    >
                      <span class="material-icons text-base">delete_outline</span>
                    </button>
                  </div>
                </header>

                <draggable
                  v-model="listTasksMap[list.id]"
                  group="tasks"
                  :animation="150"
                  item-key="id"
                  :disabled="isSyncingTasks"
                  direction="vertical"
                  :empty-insert-threshold="120"
                  :swap-threshold="0.65"
                  :invert-swap="true"
                  :fallback-on-body="true"
                  :bubble-scroll="true"
                  :scroll-sensitivity="80"
                  :scroll-speed="16"
                  :move="onTaskDragMove"
                  @start="onTaskDragStart"
                  @end="onTaskDrop"
                  @change="onTaskDragChange"
                  class="move-kanban-column__body"
                  :class="{ 'move-kanban-column__body--empty': getListTaskCount(list.id) === 0 }"
                  :data-list-id="list.id"
                  ghost-class="move-kanban-task--ghost"
                  drag-class="move-kanban-task--dragging"
                  handle=".task-drag-handle"
                >
                  <template #item="{ element: task }">
                    <div
                      class="move-kanban-task group"
                      :class="{
                        'move-kanban-task--syncing': syncingTaskIds.includes(task.id),
                        'move-kanban-task--archived': task.archived
                      }"
                      @click="openTaskModal(task)"
                    >
                        <div class="flex items-start gap-2">
                          <div class="task-drag-handle move-kanban-task__handle">
                            <span class="material-icons text-base">drag_indicator</span>
                          </div>
                          <div class="min-w-0 flex-1">
                            <div class="flex items-start gap-2 mb-1.5">
                              <span class="move-kanban-task__title">{{ task.title }}</span>
                              <span
                                v-if="task.priority"
                                class="move-kanban-priority"
                                :class="`move-kanban-priority--${task.priority}`"
                              >
                                {{ task.priority }}
                              </span>
                              <button
                                type="button"
                                class="move-kanban-task__archive"
                                :aria-label="task.archived ? 'Unarchive task' : 'Archive task'"
                                :title="task.archived ? 'Unarchive' : 'Archive'"
                                :disabled="archivingTaskId === task.id"
                                @click.stop="toggleTaskArchived(task)"
                              >
                                <span class="material-icons text-base">{{ task.archived ? 'unarchive' : 'archive' }}</span>
                              </button>
                            </div>

                            <p v-if="task.description" class="move-kanban-task__desc line-clamp-2">
                              {{ task.description }}
                            </p>

                            <div v-if="task.tags && task.tags.length > 0" class="flex flex-wrap gap-1 mb-2">
                              <span v-for="tag in task.tags.slice(0, 3)" :key="tag" class="move-kanban-tag">{{ tag }}</span>
                            </div>

                            <div class="move-kanban-task__meta">
                              <span v-if="task.dueDate" class="move-kanban-meta">
                                <span class="material-icons text-xs">event</span>
                                {{ formatDate(task.dueDate) }}
                              </span>
                              <div v-if="task.assignedTo && task.assignedTo.length > 0" class="move-kanban-task__avatars">
                                <img
                                  v-for="assignee in task.assignedTo.slice(0, 3)"
                                  :key="assignee.uid"
                                  :src="assignee.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(assignee.email)}&background=random`"
                                  :alt="assignee.email"
                                  class="move-kanban-task__avatar"
                                  :title="assignee.email"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </template>
                  <template #footer>
                    <div key="drop-zone" class="move-kanban-drop-zone">
                      <span v-if="getListTaskCount(list.id) === 0" class="move-kanban-empty">
                        {{ showArchived ? 'No archived tasks' : 'Drop tasks here' }}
                      </span>
                    </div>
                  </template>
                </draggable>

                <footer v-if="!showArchived" class="move-kanban-column__footer">
                  <button
                    v-if="addingTaskToList !== list.id"
                    type="button"
                    class="move-kanban-add-trigger"
                    @click="openAddTask(list.id)"
                  >
                    <span class="material-icons text-lg">add</span>
                    Add task
                  </button>
                  <form
                    v-else
                    class="move-kanban-add-form"
                    @submit.prevent="addTask(list.id)"
                  >
                    <textarea
                      ref="addTaskInputRef"
                      v-model="newTaskTitle[list.id]"
                      rows="2"
                      placeholder="What needs to be done?"
                      class="move-input move-kanban-add-form__input"
                      required
                      @keydown.enter.exact.prevent="addTask(list.id)"
                      @keyup.esc="closeAddTask"
                    />
                    <div class="flex gap-2">
                      <button type="submit" class="move-btn-primary flex-1 !py-2 text-sm">Add task</button>
                      <button type="button" class="move-btn-ghost !px-3" @click="closeAddTask">Cancel</button>
                    </div>
                  </form>
                </footer>
              </article>
            </transition>
          </template>
        </draggable>
      </div>

      <!-- Invite Member Modal -->
      <transition name="modal-fade">
        <div v-if="showInviteModal" class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 px-4">
          <transition name="modal-scale">
            <div class="move-card p-6 w-full max-w-md text-left">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-xl font-bold move-text">Invite Member</h3>
                <button @click="showInviteModal = false" class="move-icon-btn">
                  <span class="material-icons">close</span>
                </button>
              </div>

              <form @submit.prevent="handleInvite" class="space-y-4">
                <div class="relative">
                  <label class="block text-sm font-medium move-text mb-1">Email Address</label>
                  <div class="relative">
                    <input
                      v-model="inviteEmail"
                      type="text"
                      class="move-input"
                      placeholder="Enter email address"
                      required
                      @input="filterInviteSuggestions($event.target.value)"
                      @focus="showInviteSuggestions = true; filterInviteSuggestions(inviteEmail)"
                      @blur="handleBlur"
                    />
                    <div
                      v-if="showInviteSuggestions && inviteSuggestions.length > 0"
                      class="absolute z-[100] w-full mt-1 move-dropdown max-h-60 overflow-y-auto"
                    >
                      <div
                        v-for="user in inviteSuggestions"
                        :key="user.uid"
                        @mousedown.prevent="selectInviteSuggestion(user)"
                        class="move-dropdown-item flex items-center gap-3"
                      >
                        <img
                          :src="user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name && user.surname ? (user.name + ' ' + user.surname) : user.email)}&background=random`"
                          :alt="user.email"
                          class="w-8 h-8 rounded-full"
                        />
                        <div>
                          <div class="text-sm font-medium move-text">
                            {{ user.name && user.surname ? `${user.name} ${user.surname}` : user.email }}
                          </div>
                          <div class="text-xs move-text-muted">{{ user.email }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="pt-4">
                  <button
                    type="submit"
                    class="move-btn-primary w-full"
                    :disabled="loading"
                  >
                    <span v-if="loading" class="flex items-center justify-center gap-2">
                      <span class="material-icons animate-spin">refresh</span>
                      Inviting...
                    </span>
                    <span v-else>Send Invitation</span>
                  </button>
                </div>
              </form>

              <!-- Error Message -->
              <div v-if="error" class="move-alert-error">
                {{ error }}
              </div>

              <!-- Success Message -->
              <div v-if="success" class="rounded-lg px-3 py-2 text-sm border border-[var(--move-accent-muted)] bg-[var(--move-accent-soft)] text-[var(--move-accent)]">
                {{ success }}
              </div>
            </div>
          </transition>
        </div>
      </transition>

      <!-- Member Management Modal -->
      <transition name="modal-fade">
        <div v-if="showMembersModal" class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 px-4">
          <transition name="modal-scale">
            <div class="move-card p-8 w-full max-w-md relative text-left">
              <button @click="showMembersModal = false" class="absolute top-4 right-4 move-text text-2xl">&times;</button>
              <h2 class="text-2xl font-bold mb-4 move-text">Manage Members</h2>

              <!-- Invite Member Inline Form -->
              <form @submit.prevent="handleInvite" class="flex gap-2 mb-6">
                <div class="relative flex-1">
                  <input
                    v-model="inviteEmail"
                    type="text"
                    class="move-input"
                    placeholder="Invite by email"
                    required
                    @input="filterInviteSuggestions($event.target.value)"
                    @focus="showInviteSuggestions = true; filterInviteSuggestions(inviteEmail)"
                    @blur="handleBlur"
                  />
                  <div
                    v-if="showInviteSuggestions && inviteSuggestions.length > 0"
                    class="absolute z-[100] w-full mt-1 move-dropdown max-h-60 overflow-y-auto"
                  >
                    <div
                      v-for="user in inviteSuggestions"
                      :key="user.uid"
                      @mousedown.prevent="selectInviteSuggestion(user)"
                      class="move-dropdown-item flex items-center gap-3"
                    >
                      <img
                        :src="user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name && user.surname ? (user.name + ' ' + user.surname) : user.email)}&background=random`"
                        :alt="user.email"
                        class="w-8 h-8 rounded-full"
                      />
                      <div>
                        <div class="text-sm font-medium move-text">
                          {{ user.name && user.surname ? `${user.name} ${user.surname}` : user.email }}
                        </div>
                        <div class="text-xs move-text-muted">{{ user.email }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  class="move-btn-primary"
                  :disabled="loading"
                >
                  <span v-if="loading" class="flex items-center gap-1">
                    <span class="material-icons animate-spin">refresh</span>
                    Inviting...
                  </span>
                  <span v-else>Invite</span>
                </button>
              </form>
              <div v-if="error" class="move-alert-error mb-4">{{ error }}</div>
              <div v-if="success" class="rounded-lg px-3 py-2 text-sm border border-[var(--move-accent-muted)] bg-[var(--move-accent-soft)] text-[var(--move-accent)]">{{ success }}</div>

              <!-- Member List -->
              <ul class="mb-4 divide-y">
                <li v-for="member in filteredMembers" :key="member.uid" class="flex items-center justify-between py-3">
                  <div class="flex items-center gap-3">
                    <img
                      :src="member.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.email)}&background=random`"
                      :alt="member.email"
                      class="w-8 h-8 rounded-full border"
                    />
                    <div>
                      <div class="font-medium move-text">{{ member.email }}</div>
                      <div class="text-xs move-text-muted">
                        <span v-if="member.role === 'owner'">Owner</span>
                        <span v-else>
                          <MoveSelect
                            v-model="member.role"
                            size="inline"
                            :options="memberRoleOptions"
                            @change="changeMemberRole(member.uid, member.role)"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    v-if="canRemoveMember(member.uid)"
                    type="button"
                    @click="askRemoveMember(member)"
                    class="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded border border-red-200"
                  >Remove</button>
                </li>
              </ul>
              <div v-if="members.length === 0" class="move-text-muted">No members yet.</div>

              <!-- Member Search -->
              <input v-model="memberSearch" type="text" placeholder="Search members..." class="mb-4 move-input" />
            </div>
          </transition>
        </div>
      </transition>

      <MoveConfirmDialog
        v-model="showRemoveMemberModal"
        title="Remove member"
        subtitle="They will lose access to this board."
        :detail-title="memberToRemove?.email || ''"
        detail-tone="danger"
        confirm-label="Remove"
        confirm-icon="person_remove"
        busy-label="Removing…"
        :busy="destructiveBusy"
        :error="destructiveError"
        @confirm="confirmRemoveMember"
        @cancel="closeRemoveMemberModal"
      />

      <MoveConfirmDialog
        v-model="showDeleteListModal"
        title="Delete list"
        subtitle="This action cannot be undone."
        :detail-title="listPendingDeleteName"
        detail-body="All tasks in this list will be deleted."
        detail-tone="danger"
        confirm-label="Delete list"
        confirm-icon="delete"
        busy-label="Deleting…"
        :busy="destructiveBusy"
        :error="destructiveError"
        @confirm="confirmDeleteList"
        @cancel="closeDeleteListModal"
      />

      <!-- Task Modal -->
      <transition name="modal-fade">
        <div v-if="showTaskModal" class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 px-4 p-4">
          <transition name="modal-scale">
            <div class="move-card move-task-modal w-full max-w-2xl relative text-left">
              <header class="move-task-modal__header">
                <div class="move-task-modal__title-row">
                  <h2 class="text-xl sm:text-2xl font-bold move-text">Task Details</h2>
                  <div class="move-task-modal__title-actions">
                    <button
                      type="button"
                      class="move-task-modal__archive"
                      :disabled="!editTask.id || archivingTaskId === editTask.id"
                      @click="toggleTaskArchived(editTask)"
                    >
                      <span class="material-icons text-base">{{ editTask.archived ? 'unarchive' : 'archive' }}</span>
                      <span>{{ editTask.archived ? 'Unarchive' : 'Archive' }}</span>
                    </button>
                    <button type="button" @click="closeTaskModal" class="move-icon-btn" aria-label="Close">
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                </div>

                <div class="flex gap-2 sm:gap-4 mt-4 border-b overflow-x-auto" style="border-color: var(--move-border)">
                  <button
                    type="button"
                    @click="activeTab = 'details'"
                    class="px-3 sm:px-4 py-2 font-medium whitespace-nowrap"
                    :class="activeTab === 'details' ? 'text-[var(--move-accent)] border-b-2 border-[var(--move-accent)]' : 'move-text-muted'"
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    @click="activeTab = 'comments'"
                    class="px-3 sm:px-4 py-2 font-medium flex items-center gap-2 whitespace-nowrap"
                    :class="activeTab === 'comments' ? 'text-[var(--move-accent)] border-b-2 border-[var(--move-accent)]' : 'move-text-muted'"
                  >
                    Comments
                    <span class="move-kanban-meta">{{ taskComments.length }}</span>
                  </button>
                  <button
                    type="button"
                    @click="activeTab = 'links'"
                    class="px-3 sm:px-4 py-2 font-medium flex items-center gap-2 whitespace-nowrap"
                    :class="activeTab === 'links' ? 'text-[var(--move-accent)] border-b-2 border-[var(--move-accent)]' : 'move-text-muted'"
                  >
                    Links
                    <span class="move-kanban-meta">{{ taskLinks.length }}</span>
                  </button>
                </div>
              </header>

              <div class="move-task-modal__body custom-scrollbar">
                <!-- Details Tab -->
                <form v-if="activeTab === 'details'" id="task-edit-form" @submit.prevent="saveTaskEdit" class="flex flex-col gap-4">
                  <input v-model="editTask.title" type="text" placeholder="Title" class="move-input" required />
                  <textarea v-model="editTask.description" placeholder="Description" class="move-input" rows="3"></textarea>
                  <input v-model="editTask.dueDate" type="date" class="move-input" />
                  <input v-model="editTask.tagsString" type="text" placeholder="Tags (comma separated)" class="move-input" />

                  <div class="flex flex-col gap-2">
                    <label class="move-text font-medium">Assign to</label>
                    <div class="flex flex-wrap gap-2 mb-2">
                      <div v-for="assignee in editTask.assignedTo" :key="assignee.uid" class="flex items-center gap-1 px-2 py-1 rounded-lg" style="background: var(--move-accent-soft)">
                        <img
                          :src="assignee.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(assignee.email)}&background=random`"
                          :alt="assignee.email"
                          class="w-5 h-5 rounded-full"
                        />
                        <span class="text-sm">{{ assignee.email }}</span>
                        <button
                          type="button"
                          @click="removeAssignee(assignee)"
                          class="move-text-muted hover:text-red-500"
                        >
                          <span class="material-icons text-sm">close</span>
                        </button>
                      </div>
                    </div>
                    <MoveSelect
                      v-model="selectedAssigneeUid"
                      block
                      placeholder="Select a member to assign"
                      :options="assigneeOptions"
                      @change="addAssignee"
                    />
                  </div>

                  <div v-if="editTask.createdBy" class="flex items-center gap-3 p-3 rounded-lg" style="background: var(--move-accent-soft)">
                    <img
                      :src="editTask.createdBy.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(editTask.createdBy.name && editTask.createdBy.surname ? (editTask.createdBy.name + ' ' + editTask.createdBy.surname) : editTask.createdBy.email)}&background=random`"
                      :alt="editTask.createdBy.name && editTask.createdBy.surname ? (editTask.createdBy.name + ' ' + editTask.createdBy.surname) : editTask.createdBy.email"
                      class="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white"
                    />
                    <div>
                      <div class="text-sm move-text-muted">Created by</div>
                      <div class="font-medium move-text truncate">
                        {{ editTask.createdBy.name && editTask.createdBy.surname ?
                          `${editTask.createdBy.name} ${editTask.createdBy.surname}` :
                          editTask.createdBy.email }}
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label class="move-text font-medium">Move to List</label>
                    <MoveSelect
                      v-model="editTask.listId"
                      block
                      :options="listOptions"
                      @change="moveTaskToList"
                    />
                  </div>
                </form>

                <!-- Comments Tab -->
                <div v-else-if="activeTab === 'comments'" class="flex flex-col gap-4">
                  <div class="flex items-center justify-between">
                    <h3 class="text-lg font-medium move-text">Comments</h3>
                    <span class="text-sm move-text-muted">{{ taskComments.length }} comments</span>
                  </div>

                  <form @submit.prevent="addComment" class="flex gap-2">
                    <input
                      v-model="newComment"
                      type="text"
                      placeholder="Add a comment..."
                      class="move-input flex-1"
                      required
                    />
                    <button
                      type="submit"
                      class="move-btn-primary px-3 sm:px-4 py-2 whitespace-nowrap"
                      :disabled="!newComment.trim()"
                    >
                      Comment
                    </button>
                  </form>

                  <div v-if="taskComments.length === 0" class="text-center move-text-muted py-8">
                    No comments yet. Start the conversation!
                  </div>
                  <div v-else class="space-y-3">
                    <div
                      v-for="comment in taskComments"
                      :key="comment.id"
                      class="flex gap-3 p-3 rounded-lg"
                      style="background: var(--move-accent-soft)"
                    >
                      <img
                        :src="comment.createdBy.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.createdBy.name && comment.createdBy.surname ? (comment.createdBy.name + ' ' + comment.createdBy.surname) : comment.createdBy.email)}&background=random`"
                        :alt="comment.createdBy.name && comment.createdBy.surname ? (comment.createdBy.name + ' ' + comment.createdBy.surname) : comment.createdBy.email"
                        class="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-white flex-shrink-0"
                      />
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between gap-2">
                          <div class="font-medium move-text truncate">
                            {{ comment.createdBy.name && comment.createdBy.surname ?
                              `${comment.createdBy.name} ${comment.createdBy.surname}` :
                              comment.createdBy.email }}
                          </div>
                          <div class="flex items-center gap-2 flex-shrink-0">
                            <span class="text-xs move-text-muted whitespace-nowrap">{{ formatDate(comment.createdAt) }}</span>
                            <button
                              v-if="canDeleteComment(comment)"
                              type="button"
                              @click="deleteComment(comment.id)"
                              class="text-red-500 hover:text-red-700 text-sm whitespace-nowrap"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <p class="mt-1 move-text-muted break-words text-left">{{ comment.content }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Links Tab -->
                <div v-else-if="activeTab === 'links'" class="flex flex-col gap-4">
                  <div class="flex items-center justify-between">
                    <h3 class="text-lg font-medium move-text">Links</h3>
                    <span class="text-sm move-text-muted">{{ taskLinks.length }} links</span>
                  </div>

                  <form @submit.prevent="addLink" class="flex flex-col sm:flex-row gap-2">
                    <input
                      v-model="newLink.url"
                      type="text"
                      placeholder="Enter URL"
                      class="move-input flex-1"
                      required
                    />
                    <input
                      v-model="newLink.title"
                      type="text"
                      placeholder="Link title (optional)"
                      class="move-input flex-1"
                    />
                    <button
                      type="submit"
                      class="move-btn-primary px-3 sm:px-4 py-2 whitespace-nowrap"
                      :disabled="!newLink.url.trim()"
                    >
                      Add Link
                    </button>
                  </form>

                  <div v-if="taskLinks.length === 0" class="text-center move-text-muted py-8">
                    No links added yet.
                  </div>
                  <div v-else class="space-y-3">
                    <div
                      v-for="link in taskLinks"
                      :key="link.id"
                      class="flex items-center justify-between p-3 rounded-lg group"
                      style="background: var(--move-accent-soft)"
                    >
                      <div class="flex items-center gap-3 min-w-0">
                        <a
                          :href="link.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="flex items-center gap-2 text-[var(--move-accent)] hover:opacity-80 transition truncate"
                        >
                          <span class="material-icons">link</span>
                          <span class="truncate">{{ link.title || link.url }}</span>
                        </a>
                        <span class="text-xs move-text-muted">{{ formatDate(link.createdAt) }}</span>
                      </div>
                      <button
                        type="button"
                        @click="deleteLink(link.id)"
                        class="text-red-500 hover:text-red-700 text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <footer v-if="activeTab === 'details'" class="move-task-modal__footer">
                <button type="submit" form="task-edit-form" class="move-btn-primary flex-1">Save</button>
                <button type="button" class="move-btn-ghost flex-1 !text-[var(--move-danger)] !border-[var(--move-danger)]/30" @click="deleteTask(editTask.id)">Delete</button>
              </footer>
            </div>
          </transition>
        </div>
      </transition>

      <MoveConfirmDialog
        v-model="showDeleteTaskModal"
        title="Delete task"
        subtitle="This action cannot be undone."
        :detail-title="taskPendingDeleteTitle"
        detail-body="Comments and links on this task will be removed too."
        detail-tone="danger"
        confirm-label="Delete task"
        confirm-icon="delete"
        busy-label="Deleting…"
        :busy="destructiveBusy"
        :error="destructiveError"
        @confirm="confirmDeleteTask"
        @cancel="closeDeleteTaskModal"
      />

      <MoveConfirmDialog
        v-model="showDeleteCommentModal"
        title="Delete comment"
        subtitle="This action cannot be undone."
        :detail-title="commentPendingDeletePreview"
        detail-tone="danger"
        confirm-label="Delete comment"
        confirm-icon="delete"
        busy-label="Deleting…"
        :busy="destructiveBusy"
        :error="destructiveError"
        @confirm="confirmDeleteComment"
        @cancel="closeDeleteCommentModal"
      />

      <MoveConfirmDialog
        v-model="showDeleteLinkModal"
        title="Delete link"
        subtitle="This action cannot be undone."
        :detail-title="linkPendingDeleteLabel"
        :detail-body="linkPendingDeleteUrl"
        detail-tone="danger"
        confirm-label="Delete link"
        confirm-icon="link_off"
        busy-label="Deleting…"
        :busy="destructiveBusy"
        :error="destructiveError"
        @confirm="confirmDeleteLink"
        @cancel="closeDeleteLinkModal"
      />
    </div>
    <div v-else class="flex items-center justify-center py-24">
      <div class="text-center">
        <div class="move-spinner mx-auto mb-4" aria-label="Loading board" />
        <p class="move-text-muted">Loading board...</p>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useStore } from 'vuex'
import { moveApi } from '../services/moveApi'
import { loadBoardPayload, mapMemberFromApi } from '../utils/moveData'
import AppLayout from '../layouts/AppLayout.vue'
import MoveConfirmDialog from '../components/MoveConfirmDialog.vue'
import draggable from 'vuedraggable'

const route = useRoute()
const store = useStore()
const boardId = computed(() => route.params.boardId)
const currentUserId = computed(() => store.getters['auth/currentUserId'])

// State management
const board = ref(null)
const loadingBoard = ref(true)
const lists = ref([])
const tasks = ref([])
const members = ref([])
const currentBoardId = ref(null)
const showInviteModal = ref(false)
const showMembersModal = ref(false)
const showTaskModal = ref(false)
const showDeleteListModal = ref(false)
const showDeleteTaskModal = ref(false)
const showDeleteCommentModal = ref(false)
const showDeleteLinkModal = ref(false)
const listToDelete = ref(null)
const taskToDelete = ref(null)
const destructiveBusy = ref(false)
const destructiveError = ref('')
const showRemoveMemberModal = ref(false)
const memberToRemove = ref(null)
const inviteEmail = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')
const showInviteSuggestions = ref(false)
const inviteSuggestions = ref([])
const allUsers = ref([])
const newListName = ref('')
const newTaskTitle = reactive({})
const addingTaskToList = ref(null)
const addTaskInputRef = ref(null)
const editTask = reactive({})
const memberSearch = ref('')
const newComment = ref('')
const taskComments = ref([])
const activeTab = ref('details')
const commentToDelete = ref(null)
const taskLinks = ref([])
const newLink = ref({
  url: '',
  title: ''
})
const linkToDelete = ref(null)
const isEditingBoardName = ref(false)
const newBoardName = ref('')
const selectedAssigneeUid = ref('')
const listTasksMap = reactive({})
const isSyncingTasks = ref(false)
const syncingListIds = ref([])
const syncingTaskIds = ref([])
const taskDropInProgress = ref(false)
const dragOverListId = ref(null)
const showArchived = ref(false)
const archivingTaskId = ref(null)
const editingListId = ref(null)
const editingListName = ref('')
const listNameInput = ref(null)
const savingListName = ref(false)

const memberRoleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' }
]

const rebuildListTasksMap = () => {
  lists.value.forEach((list) => {
    listTasksMap[list.id] = tasks.value
      .filter((task) => task.listId === list.id)
      .filter((task) => (showArchived.value ? task.archived : !task.archived))
      .sort((a, b) => (a.position || 0) - (b.position || 0))
  })
}

const syncTasksFromListMap = () => {
  const updated = []
  const seen = new Set()
  lists.value.forEach((list) => {
    (listTasksMap[list.id] || []).forEach((task, index) => {
      task.listId = list.id
      task.position = index
      updated.push(task)
      seen.add(String(task.id))
    })
  })
  tasks.value.forEach((task) => {
    if (!seen.has(String(task.id))) {
      updated.push(task)
    }
  })
  tasks.value = updated
}

const isListSyncing = (listId) => syncingListIds.value.includes(listId)

// Add this computed property
const isMobile = computed(() => {
  return window.innerWidth < 640 // sm breakpoint
})

// Fetch board, lists, tasks, members
const applyBoardPayload = (payload) => {
  if (payload.board?.name) {
    document.title = `${payload.board.name} - Tiye Move`
  }
  board.value = {
    ...payload.board,
    members: (payload.board.members || []).map(mapMemberFromApi)
  }
  lists.value = payload.lists
  tasks.value = payload.tasks
  members.value = board.value.members.map((member) => ({
    uid: member.uid,
    email: member.email,
    name: member.name,
    surname: member.surname,
    photoURL: member.photoURL,
    role: member.role
  }))
  allUsers.value = payload.users
  rebuildListTasksMap()
}

const reloadBoardData = async (id) => {
  const payload = await loadBoardPayload(id)
  applyBoardPayload(payload)
}

const fetchBoard = async (id) => {
  await reloadBoardData(id)
}

const fetchListsAndTasks = async () => Promise.resolve()

const cleanup = () => {
  board.value = null
  lists.value = []
  tasks.value = []
  members.value = []
}

// Load board function
const loadBoard = async (id) => {
  console.log('Loading board:', id) // Debug log

  // Don't reload if it's the same board
  if (currentBoardId.value === id) {
    console.log('Same board, skipping reload') // Debug log
    return
  }

  loadingBoard.value = true
  currentBoardId.value = id

  try {
    // Clean up existing data
    cleanup()

    // Load new board data
    console.log('Loading new board data') // Debug log
    await fetchBoard(id)
    await fetchListsAndTasks(id)

    // Wait for next tick to ensure DOM updates
    await nextTick()
    console.log('Board loading complete') // Debug log
  } catch (error) {
    console.error('Error loading board:', error)
    cleanup() // Clean up on error
  } finally {
    // Add a small delay before hiding the loading state
    setTimeout(() => {
      loadingBoard.value = false
      console.log('Loading state set to false') // Debug log
    }, 100)
  }
}

// Handle route updates
onBeforeRouteUpdate(async (to, from) => {
  console.log('Route updating:', { to, from }) // Debug log
  if (to.name === 'board' && to.params.boardId) {
    await loadBoard(to.params.boardId)
  }
})

// Watch for board ID changes
watch(
  () => route.params.boardId,
  async (newId, oldId) => {
    console.log('Board ID changed:', { newId, oldId }) // Debug log
    if (newId && newId !== oldId) {
      await loadBoard(newId)
    }
  }
)

onMounted(async () => {
  window.addEventListener('keydown', handleKeyDown)
  await loadBoard(boardId.value)
  openTaskFromQuery()
})

const openTaskFromQuery = async () => {
  const taskId = route.query.task
  if (!taskId) return

  await nextTick()
  const task = tasks.value.find((t) => String(t.id) === String(taskId))
  if (task) {
    if (task.archived) {
      showArchived.value = true
    }
    openTaskModal(task)
  }
}

watch(
  () => route.query.task,
  async (taskId) => {
    if (!taskId || loadingBoard.value) return
    await openTaskFromQuery()
  }
)

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  cleanup()
})

const addList = async () => {
  if (!newListName.value.trim()) return
  await moveApi.createList(boardId.value, newListName.value.trim())
  await reloadBoardData(boardId.value)
  newListName.value = ''
}

const confirmDeleteList = async () => {
  if (!listToDelete.value || destructiveBusy.value) return
  try {
    destructiveBusy.value = true
    destructiveError.value = ''
    await moveApi.deleteList(boardId.value, listToDelete.value)
    await reloadBoardData(boardId.value)
    showDeleteListModal.value = false
    listToDelete.value = null
  } catch (error) {
    console.error('Error deleting list:', error)
    destructiveError.value = error.response?.data?.message || 'Failed to delete this list.'
  } finally {
    destructiveBusy.value = false
  }
}

const addTask = async (listId) => {
  const title = newTaskTitle[listId]
  if (!title || !title.trim()) return

  try {
    const currentTasks = listTasksMap[listId] || []
    await moveApi.createTask(boardId.value, {
      list_id: listId,
      title: title.trim(),
      position: currentTasks.length
    })
    await reloadBoardData(boardId.value)
    newTaskTitle[listId] = ''
    addingTaskToList.value = null
  } catch (error) {
    console.error('Error adding task:', error)
  }
}

const getListTaskCount = (listId) => (listTasksMap[listId] || []).length

const openAddTask = async (listId) => {
  addingTaskToList.value = listId
  await nextTick()
  addTaskInputRef.value?.focus()
  addTaskInputRef.value?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
}

const closeAddTask = () => {
  if (addingTaskToList.value !== null) {
    newTaskTitle[addingTaskToList.value] = ''
  }
  addingTaskToList.value = null
}

const onTaskDragStart = () => {
  document.body.classList.add('select-none', 'move-kanban-dragging')
}

const onTaskDragChange = (evt) => {
  if (evt?.to) {
    dragOverListId.value = Number(evt.to.getAttribute('data-list-id')) || null
  }
}

const onTaskDragMove = (evt) => {
  if (evt?.to) {
    dragOverListId.value = Number(evt.to.getAttribute('data-list-id')) || null
  }
  return true
}

const onTaskDrop = async (evt) => {
  document.body.classList.remove('select-none', 'move-kanban-dragging')
  dragOverListId.value = null

  if (!evt?.to || !evt?.from) return
  if (evt.from === evt.to && evt.oldIndex === evt.newIndex) return
  if (taskDropInProgress.value) return

  const sourceListId = Number(evt.from.getAttribute('data-list-id'))
  const targetListId = Number(evt.to.getAttribute('data-list-id'))
  if (!sourceListId || !targetListId) return

  taskDropInProgress.value = true
  syncTasksFromListMap()

  const affectedListIds = sourceListId === targetListId
    ? [targetListId]
    : [sourceListId, targetListId]

  const tasksToUpdate = []
  affectedListIds.forEach((listId) => {
    (listTasksMap[listId] || []).forEach((task, index) => {
      tasksToUpdate.push({ task, listId, index })
    })
  })

  syncingListIds.value = affectedListIds
  syncingTaskIds.value = tasksToUpdate.map(({ task }) => task.id)
  isSyncingTasks.value = true

  try {
    await Promise.all(
      tasksToUpdate.map(({ task, listId, index }) =>
        moveApi.updateTask(boardId.value, task.id, {
          list_id: listId,
          position: index
        })
      )
    )
    syncTasksFromListMap()
  } catch (error) {
    console.error('Error updating task positions:', error)
    await reloadBoardData(boardId.value)
  } finally {
    isSyncingTasks.value = false
    syncingListIds.value = []
    syncingTaskIds.value = []
    taskDropInProgress.value = false
  }
}

const saveTaskEdit = async () => {
  if (!editTask.title.trim()) return
  await moveApi.updateTask(boardId.value, editTask.id, {
    title: editTask.title,
    description: editTask.description,
    due_date: editTask.dueDate || null,
    tags: (editTask.tagsString || '').split(',').map(t => t.trim()).filter(Boolean),
    assigned_to: (Array.isArray(editTask.assignedTo) ? editTask.assignedTo : [])
      .map((assignee) => Number(assignee.uid || assignee.user_id || assignee.id))
      .filter(Boolean),
    list_id: editTask.listId
  })
  await reloadBoardData(boardId.value)
  closeTaskModal()
}

const confirmDeleteTask = async () => {
  if (!taskToDelete.value || destructiveBusy.value) return
  try {
    destructiveBusy.value = true
    destructiveError.value = ''
    await moveApi.deleteTask(boardId.value, taskToDelete.value)
    showDeleteTaskModal.value = false
    taskToDelete.value = null
    closeTaskModal()
    await reloadBoardData(boardId.value)
  } catch (error) {
    console.error('Error deleting task:', error)
    destructiveError.value = error.response?.data?.message || 'Failed to delete this task.'
  } finally {
    destructiveBusy.value = false
  }
}

const changeMemberRole = async (uid, newRole) => {
  if (!board.value || !uid || !newRole) return
  try {
    await moveApi.updateMember(boardId.value, Number(uid), newRole)
    await reloadBoardData(boardId.value)
  } catch (error) {
    console.error('Error changing member role:', error)
  }
}

const handleInvite = async () => {
  try {
    loading.value = true
    error.value = ''

    if (!inviteEmail.value.trim()) {
      error.value = 'Please enter an email address'
      return
    }

    const invitedUser = allUsers.value.find(
      (user) => user.email?.toLowerCase() === inviteEmail.value.trim().toLowerCase()
    )

    if (!invitedUser) {
      error.value = 'User not found'
      return
    }

    if (board.value.members?.some((member) => String(member.uid) === String(invitedUser.uid))) {
      error.value = 'User is already a member of this board'
      return
    }

    await moveApi.addMember(boardId.value, Number(invitedUser.uid || invitedUser.id), 'member')
    await reloadBoardData(boardId.value)
    inviteEmail.value = ''
    success.value = 'User invited successfully'
  } catch (err) {
    console.error('Error inviting user:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to invite user'
  } finally {
    loading.value = false
  }
}

const askRemoveMember = (member) => {
  memberToRemove.value = member
  destructiveError.value = ''
  showRemoveMemberModal.value = true
}

const closeRemoveMemberModal = () => {
  showRemoveMemberModal.value = false
  memberToRemove.value = null
  destructiveError.value = ''
}

const confirmRemoveMember = async () => {
  const member = memberToRemove.value
  if (!board.value || !member?.uid || destructiveBusy.value) return
  try {
    destructiveBusy.value = true
    destructiveError.value = ''
    await moveApi.removeMember(boardId.value, Number(member.uid))
    showRemoveMemberModal.value = false
    memberToRemove.value = null
    await reloadBoardData(boardId.value)
  } catch (error) {
    console.error('Error removing member:', error)
    destructiveError.value = error.response?.data?.message || 'Failed to remove this member.'
  } finally {
    destructiveBusy.value = false
  }
}

const canRemoveMember = (uid) => {
  if (!board.value || !currentUserId.value || !uid) return false
  return board.value.is_owner && String(uid) !== String(currentUserId.value)
}

const moveTaskToList = async () => {
  try {
    const currentListTasks = listTasksMap[editTask.listId] || []
    await moveApi.updateTask(boardId.value, editTask.id, {
      list_id: editTask.listId,
      position: currentListTasks.length
    })
  } catch (error) {
    console.error('Error moving task:', error)
  }
}

const onListDrop = async () => {
  try {
    await moveApi.reorderLists(
      boardId.value,
      lists.value.map((list, index) => ({ id: list.id, position: index }))
    )
    await reloadBoardData(boardId.value)
  } catch (error) {
    console.error('Error updating list positions:', error)
    await reloadBoardData(boardId.value)
  }
}

const fetchComments = async (taskId) => {
  try {
    const { data } = await moveApi.listComments(taskId)
    taskComments.value = (data.data || []).map((comment) => ({
      id: comment.id,
      ...comment,
      createdBy: comment.created_by || comment.createdBy
    }))
  } catch (error) {
    console.error('Error loading comments:', error)
    taskComments.value = []
  }
}

const addComment = async () => {
  if (!newComment.value.trim() || !editTask.id) return
  try {
    await moveApi.createComment(editTask.id, newComment.value.trim())
    newComment.value = ''
    await fetchComments(editTask.id)
  } catch (error) {
    console.error('Error adding comment:', error)
  }
}

const confirmDeleteComment = async () => {
  if (!commentToDelete.value || destructiveBusy.value) return
  try {
    destructiveBusy.value = true
    destructiveError.value = ''
    await moveApi.deleteComment(commentToDelete.value)
    closeDeleteCommentModal()
    await fetchComments(editTask.id)
  } catch (error) {
    console.error('Error deleting comment:', error)
    destructiveError.value = error.response?.data?.message || 'Failed to delete this comment.'
  } finally {
    destructiveBusy.value = false
  }
}

const canDeleteComment = (comment) => {
  const creatorId = comment.createdBy?.uid || comment.created_by?.uid
  return currentUserId.value && (
    String(creatorId) === String(currentUserId.value) ||
    board.value?.is_owner
  )
}

const fetchLinks = async (taskId) => {
  try {
    const { data } = await moveApi.listLinks(taskId)
    taskLinks.value = (data.data || []).map((link) => ({
      id: link.id,
      ...link,
      createdBy: link.created_by || link.createdBy
    }))
  } catch (error) {
    console.error('Error loading links:', error)
    taskLinks.value = []
  }
}

const addLink = async () => {
  const url = newLink.value.url.trim()
  if (!url || !editTask.id) return

  try {
    let finalUrl = url
    if (!finalUrl.match(/^https?:\/\//i)) {
      finalUrl = 'https://' + finalUrl
    }

    await moveApi.createLink(editTask.id, {
      url: finalUrl,
      title: newLink.value.title.trim()
    })
    newLink.value = { url: '', title: '' }
    await fetchLinks(editTask.id)
  } catch (error) {
    console.error('Error adding link:', error)
  }
}

const confirmDeleteLink = async () => {
  if (!linkToDelete.value || destructiveBusy.value) return
  try {
    destructiveBusy.value = true
    destructiveError.value = ''
    await moveApi.deleteLink(linkToDelete.value)
    closeDeleteLinkModal()
    await fetchLinks(editTask.id)
  } catch (error) {
    console.error('Error deleting link:', error)
    destructiveError.value = error.response?.data?.message || 'Failed to delete this link.'
  } finally {
    destructiveBusy.value = false
  }
}

const saveBoardName = async () => {
  if (!newBoardName.value.trim() || !board.value) return
  try {
    await moveApi.updateBoard(boardId.value, { name: newBoardName.value.trim() })
    board.value.name = newBoardName.value.trim()
    isEditingBoardName.value = false
  } catch (error) {
    console.error('Error updating board name:', error)
  }
}

const isBoardOwner = computed(() => board.value?.is_owner === true)

const activeTaskCount = computed(() => tasks.value.filter((task) => !task.archived).length)
const archivedTaskCount = computed(() => tasks.value.filter((task) => task.archived).length)

watch(showArchived, () => {
  closeAddTask()
  rebuildListTasksMap()
})

const toggleTaskArchived = async (task) => {
  if (!task?.id || archivingTaskId.value) return

  const nextArchived = !task.archived
  archivingTaskId.value = task.id

  try {
    await moveApi.updateTask(boardId.value, task.id, { archived: nextArchived })
    if (showTaskModal.value && String(editTask.id) === String(task.id) && nextArchived !== showArchived.value) {
      closeTaskModal()
    }
    await reloadBoardData(boardId.value)
  } catch (error) {
    console.error('Error updating task archive state:', error)
  } finally {
    archivingTaskId.value = null
  }
}

const openTaskModal = (task) => {
  const assignedTo = (task.assignedTo || []).map((id) => {
    const member = members.value.find((m) => String(m.uid) === String(id))
    return member || { uid: String(id) }
  })

  Object.assign(editTask, {
    ...task,
    tagsString: (task.tags || []).join(', '),
    listId: task.listId,
    assignedTo
  })
  showTaskModal.value = true
  fetchComments(task.id)
  fetchLinks(task.id)
}

const closeTaskModal = () => {
  showTaskModal.value = false
  taskComments.value = []
  taskLinks.value = []
  newComment.value = ''
  newLink.value = { url: '', title: '' }
  activeTab.value = 'details'
}

const deleteTask = (taskId) => {
  taskToDelete.value = taskId
  destructiveError.value = ''
  showDeleteTaskModal.value = true
}

const closeDeleteTaskModal = () => {
  showDeleteTaskModal.value = false
  taskToDelete.value = null
  destructiveError.value = ''
}

const deleteList = (listId) => {
  listToDelete.value = listId
  destructiveError.value = ''
  showDeleteListModal.value = true
}

const closeDeleteListModal = () => {
  showDeleteListModal.value = false
  listToDelete.value = null
  destructiveError.value = ''
}

const filteredMembers = computed(() => {
  if (!memberSearch.value) return members.value
  return members.value.filter(m =>
    m.email.toLowerCase().includes(memberSearch.value.toLowerCase())
  )
})

const truncatePreview = (value, max = 160) => {
  const text = String(value || '').trim()
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max).trim()}…` : text
}

const listPendingDeleteName = computed(() =>
  lists.value.find((list) => String(list.id) === String(listToDelete.value))?.name || 'This list'
)

const taskPendingDeleteTitle = computed(() => {
  const fromBoard = tasks.value.find((task) => String(task.id) === String(taskToDelete.value))
  if (fromBoard?.title) return fromBoard.title
  if (String(editTask.id) === String(taskToDelete.value) && editTask.title) return editTask.title
  return 'This task'
})

const commentPendingDeletePreview = computed(() => {
  const comment = taskComments.value.find((item) => String(item.id) === String(commentToDelete.value))
  return truncatePreview(comment?.content) || 'This comment will be removed from the task.'
})

const linkPendingDelete = computed(() =>
  taskLinks.value.find((item) => String(item.id) === String(linkToDelete.value)) || null
)

const linkPendingDeleteLabel = computed(() =>
  linkPendingDelete.value?.title || linkPendingDelete.value?.url || 'This link will be removed from the task.'
)

const linkPendingDeleteUrl = computed(() =>
  linkPendingDelete.value?.title ? (linkPendingDelete.value.url || '') : ''
)

const formatDate = (date) => {
  if (!date) return ''
  const timestamp = date?.toDate ? date.toDate() : date
  const d = new Date(timestamp)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const deleteComment = (commentId) => {
  commentToDelete.value = commentId
  destructiveError.value = ''
  showDeleteCommentModal.value = true
}

const closeDeleteCommentModal = () => {
  showDeleteCommentModal.value = false
  commentToDelete.value = null
  destructiveError.value = ''
}

const deleteLink = (linkId) => {
  linkToDelete.value = linkId
  destructiveError.value = ''
  showDeleteLinkModal.value = true
}

const closeDeleteLinkModal = () => {
  showDeleteLinkModal.value = false
  linkToDelete.value = null
  destructiveError.value = ''
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && showTaskModal.value) {
    closeTaskModal()
  }
}

const filterInviteSuggestions = (query) => {
  if (!query) {
    inviteSuggestions.value = []
    return
  }

  const lowercaseQuery = query.toLowerCase()
  inviteSuggestions.value = allUsers.value
    .filter(user => {
      if (board.value?.members?.some(m => String(m.uid) === String(user.uid))) {
        return false
      }
      return user.email.toLowerCase().includes(lowercaseQuery) ||
        (user.name && user.name.toLowerCase().includes(lowercaseQuery)) ||
        (user.surname && user.surname.toLowerCase().includes(lowercaseQuery))
    })
    .slice(0, 5)
}

const selectInviteSuggestion = (user) => {
  inviteEmail.value = user.email
  showInviteSuggestions.value = false
}

const handleBlur = () => {
  setTimeout(() => {
    showInviteSuggestions.value = false
  }, 200)
}

const startEditingBoardName = () => {
  if (!isBoardOwner.value) return
  newBoardName.value = board.value.name
  isEditingBoardName.value = true
}

const cancelEditingBoardName = () => {
  isEditingBoardName.value = false
  newBoardName.value = ''
}

const startEditingListName = async (list) => {
  if (!list?.id) return
  editingListId.value = list.id
  editingListName.value = list.name
  await nextTick()
  listNameInput.value?.focus()
  listNameInput.value?.select()
}

const cancelEditingListName = () => {
  editingListId.value = null
  editingListName.value = ''
}

const saveListName = async (listId) => {
  if (editingListId.value !== listId || savingListName.value) return

  const list = lists.value.find((item) => item.id === listId)
  const name = editingListName.value.trim()
  if (!list || !name || name === list.name) {
    cancelEditingListName()
    return
  }

  savingListName.value = true
  try {
    await moveApi.updateList(boardId.value, listId, { name })
    list.name = name
    cancelEditingListName()
  } catch (error) {
    console.error('Error renaming list:', error)
    await nextTick()
    listNameInput.value?.focus()
  } finally {
    savingListName.value = false
  }
}

const moveListUp = async (listId, index) => {
  if (index === 0) return
  const newLists = [...lists.value]
  const temp = newLists[index]
  newLists[index] = newLists[index - 1]
  newLists[index - 1] = temp
  lists.value = newLists
  await onListDrop()
}

const moveListDown = async (listId, index) => {
  if (index === lists.value.length - 1) return
  const newLists = [...lists.value]
  const temp = newLists[index]
  newLists[index] = newLists[index + 1]
  newLists[index + 1] = temp
  lists.value = newLists
  await onListDrop()
}

const availableAssignees = computed(() => {
  const assignedTo = Array.isArray(editTask.assignedTo) ? editTask.assignedTo : []
  return members.value.filter(member =>
    !assignedTo.some(assignee => assignee && String(assignee.uid) === String(member.uid))
  )
})

const assigneeOptions = computed(() =>
  availableAssignees.value.map((member) => ({
    value: member.uid,
    label: member.email
  }))
)

const listOptions = computed(() =>
  lists.value.map((list) => ({
    value: list.id,
    label: list.name
  }))
)

const addAssignee = () => {
  if (!selectedAssigneeUid.value) return
  const member = members.value.find((m) => String(m.uid) === String(selectedAssigneeUid.value))
  if (!member) return
  if (!Array.isArray(editTask.assignedTo)) {
    editTask.assignedTo = []
  }
  if (!editTask.assignedTo.some(a => a && String(a.uid) === String(member.uid))) {
    editTask.assignedTo.push(member)
  }
  selectedAssigneeUid.value = ''
}

const removeAssignee = (assignee) => {
  if (!Array.isArray(editTask.assignedTo)) {
    editTask.assignedTo = []
    return
  }
  editTask.assignedTo = editTask.assignedTo.filter(a => a && String(a.uid) !== String(assignee.uid))
}
</script>

<style scoped>
/* List animations */
.list-slide-enter-active {
  animation: list-slide-in 0.3s ease-out;
}

.list-slide-leave-active {
  animation: list-slide-out 0.3s ease-in;
}

@keyframes list-slide-in {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes list-slide-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(20px);
  }
}

/* Task animations */
.task-slide-enter-active {
  animation: task-slide-in 0.3s ease-out;
}

.task-slide-leave-active {
  animation: task-slide-out 0.3s ease-in;
}

@keyframes task-slide-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes task-slide-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

/* Modal animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Hover lift effect */
.hover-lift {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Active press effect */
.active-press {
  transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
}

.active-press:active {
  transform: scale(0.98) translateY(1px);
}

/* Fade animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.3s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Add this to your existing styles */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--move-accent-muted) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--move-accent-muted);
  border-radius: 3px;
}

/* Add these new styles */
.material-icons {
  font-size: 18px;
  line-height: 1;
}

@media (min-width: 640px) {
  .material-icons {
    font-size: 24px;
  }
}
</style>
